import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Invoice, CreateInvoiceInput, UpdateInvoiceInput } from "../types";

const INVOICE_FIELDS = `
  id
  organizationId
  invoiceNumber
  saleId
  customerId
  invoiceDate
  dueDate
  items {
    productId
    productName
    quantity
    unitPrice
    lineTotal
  }
  subtotal
  taxPercentage
  taxAmount
  total
  amountPaid
  status
  notes
  createdAt
  updatedAt
`;

export const invoiceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvoices: builder.query<Invoice[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Invoices($organizationId: String) {
              invoices(organizationId: $organizationId) {
                ${INVOICE_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Invoice[]>(res, "invoices"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((i) => ({ type: "Invoice" as const, id: i.id })),
              { type: "Invoice" as const, id: "LIST" },
            ]
          : [{ type: "Invoice" as const, id: "LIST" }],
    }),

    getInvoice: builder.query<Invoice, string>({
      query: (id) => ({
        body: {
          query: `
            query Invoice($id: String!) {
              invoice(id: $id) {
                ${INVOICE_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Invoice>(res, "invoice"),

      providesTags: (_result, _err, id) => [{ type: "Invoice", id }],
    }),

    createInvoice: builder.mutation<Invoice, CreateInvoiceInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateInvoice($input: CreateInvoiceInput!) {
              createInvoice(input: $input) {
                ${INVOICE_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Invoice>(res, "createInvoice"),

      invalidatesTags: [{ type: "Invoice", id: "LIST" }],
    }),

    updateInvoice: builder.mutation<
      Invoice,
      { id: string; input: UpdateInvoiceInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateInvoice($id: String!, $input: UpdateInvoiceInput!) {
              updateInvoice(id: $id, input: $input) {
                ${INVOICE_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Invoice>(res, "updateInvoice"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Invoice", id },
        { type: "Invoice", id: "LIST" },
      ],
    }),

    deleteInvoice: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteInvoice($id: String!) {
              deleteInvoice(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteInvoice"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Invoice", id },
        { type: "Invoice", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
} = invoiceApi;
